import React, { useState, useEffect, CSSProperties, useRef } from "react";
import { Box, SxProps } from "@mui/material";
import { StyledTab, StyledTabs } from "./Tab";
import { useNavigate, useLocation } from "react-router-dom";
import TabPanel from "../helpers/TabPanel";

export interface PanelProps {
  title: string | React.ReactNode;
  tabName: string;
  fullHeight?: boolean;
  panelSx?: SxProps;
}

interface TabbedDetailsPanelProps {
  panels: React.ReactElement<PanelProps>[];
  defaultTab?: number;
  sx?: CSSProperties;
}

export default ({ panels, defaultTab = 0, sx }: TabbedDetailsPanelProps) => {
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(defaultTab);
  const { hash } = useLocation();
  const parentContainer = useRef<HTMLDivElement>(null);
  const scrollAreaRef = useRef<HTMLDivElement>(null);

  // Create a mapping from tab name to tab index location
  const tabNameToIndex = panels.reduce(
    (acc, panel, index) => {
      acc[panel.props.tabName] = index;
      return acc;
    },
    {} as Record<string, number>,
  );

  useEffect(() => {
    const tabName = hash.slice(1).split("/")[0];
    const tab =
      tabName && tabNameToIndex[tabName] ? tabNameToIndex[tabName] : defaultTab;
    setTabValue(tab);
  }, [hash]);

  useEffect(() => {
    scrollAreaRef.current?.focus?.();
  }, []);

  return (
    <Box
      ref={parentContainer}
      sx={{
        minWidth: "100px",
        gap: "24px",
        alignSelf: "stretch",
        padding: "0px 20px 0 20px",
        overflowY: "scroll",
        display: "flex",
        flexDirection: "column",
        height: "100%",
        ...sx,
      }}
    >
      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
        <StyledTabs
          value={tabValue}
          variant="scrollable"
          scrollButtons="auto"
          onChange={(_: React.SyntheticEvent, newValue: number) => {
            setTabValue(newValue);
            navigate(`#${panels[newValue].props.tabName}`, { replace: true });
          }}
        >
          {panels.map((panel, i) => (
            <StyledTab
              key={i}
              label={panel.props.title}
              id={panel.props.tabName}
            />
          ))}
        </StyledTabs>
      </Box>
      <Box
        ref={scrollAreaRef}
        sx={{
          flex: "1 1 100px",
          overflowY: "auto",
          "&::-webkit-scrollbar": {
            display: "none",
          },
          outline: "none",
        }}
        tabIndex={0}
      >
        {panels.map((panel, idx) => {
          return (
            <TabPanel
              children={panel}
              index={idx}
              key={idx}
              value={tabValue}
              fullHeight={panel.props.fullHeight}
              panelSx={panel.props.panelSx}
            />
          );
        })}
      </Box>
    </Box>
  );
};
